import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, AlertTriangle, Clock, Handshake, CheckCheck } from 'lucide-react'
import Tooltip from '@/components/ui/Tooltip'
import { useNotifications } from '@/hooks/useNotifications'
import type { NotificationType } from '@/hooks/useNotifications'
import { formatDate } from '@/lib/utils'
import { formatDistanceToNow, parseISO, isValid } from 'date-fns'
import { fr } from 'date-fns/locale'

const TYPE_CONFIG: Record<NotificationType, { icon: React.ReactNode; color: string; label: string }> = {
  relance_due: {
    icon: <Clock size={14} />,
    color: 'text-orange-500',
    label: 'Relance',
  },
  mission_ending: {
    icon: <AlertTriangle size={14} />,
    color: 'text-amber-500',
    label: 'Mission',
  },
  pe_ending: {
    icon: <AlertTriangle size={14} />,
    color: 'text-purple-500',
    label: 'PE',
  },
  collaboration_accepted: {
    icon: <Handshake size={14} />,
    color: 'text-green-500',
    label: 'Collaboration',
  },
  new_intake: {
    icon: <Bell size={14} />,
    color: 'text-indigo-500',
    label: 'Besoin client',
  },
}

function relativeTime(dateStr: string): string {
  try {
    const d = parseISO(dateStr)
    if (!isValid(d)) return formatDate(dateStr)
    return formatDistanceToNow(d, { addSuffix: true, locale: fr })
  } catch {
    return formatDate(dateStr)
  }
}

export default function NotificationCenter() {
  const navigate = useNavigate()
  const { notifications, unreadCount, markAsRead, markAllAsRead, loading } = useNotifications()
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  function handleNotificationClick(n: import('@/hooks/useNotifications').AppNotification) {
    markAsRead(n.id)
    setOpen(false)
    if (n.link) {
      navigate(n.link)
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Cloche */}
      <Tooltip content={unreadCount > 0 ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}` : 'Notifications'} position="bottom">
        <button
          onClick={() => setOpen((v) => !v)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition relative"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </Tooltip>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-xs font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">
                  {unreadCount}
                </span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs text-[#4F46E5] hover:text-indigo-700 font-medium transition"
              >
                <CheckCheck size={12} />
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Liste */}
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="py-6 flex justify-center">
                <div className="w-5 h-5 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell size={24} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">Aucune notification</p>
              </div>
            ) : (
              notifications.map((n) => {
                const config = TYPE_CONFIG[n.type] ?? TYPE_CONFIG.relance_due
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 dark:border-slate-700/50 cursor-pointer transition hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-slate-700/50 ${
                      n.read ? 'opacity-60' : 'bg-indigo-50/30 dark:bg-indigo-900/10'
                    }`}
                  >
                    {/* Icône type */}
                    <div className={`mt-0.5 shrink-0 ${config.color}`}>
                      {config.icon}
                    </div>

                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${n.read ? 'text-gray-600 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100'} truncate`}>
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="text-xs text-gray-400 dark:text-slate-500 truncate mt-0.5">{n.body}</p>
                      )}
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">{relativeTime(n.created_at)}</p>
                    </div>

                    {/* Marquer lu */}
                    {!n.read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markAsRead(n.id) }}
                        className="shrink-0 p-1 rounded-lg hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600 transition"
                        title="Marquer comme lu"
                      >
                        <Check size={12} />
                      </button>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
